import { useEffect, type ReactNode } from 'react';

import { OptimizelyProvider, createInstance, type ReactSDKClient } from '@optimizely/react-sdk';

import { newRelic } from '../newRelic/newRelic';
import { useUserDataForFeatureFlag } from './useUserDataForFeatureFlag';

declare global {
  interface Window {
    _optimizelyClient: ReactSDKClient | undefined;
  }
}

/**
 * errorHandler is an object that contains a method called handleError.
 * This method is used to handle errors thrown in Optimizely SDK.
 * It takes an Error object as a parameter and sends the error stack trace to New Relic.
 */
export const errorHandler = {
  handleError: (error: Error) => {
    newRelic.noticeError('Optimizely error', { error });
  },
};

const WARNING = 3;
export const optimizelyClient = createInstance({
  datafileOptions: {
    autoUpdate: true,
  },
  errorHandler,
  eventBatchSize: 10,
  eventFlushInterval: 1000,
  logLevel: WARNING,
  sdkKey: CONFIG.optimizely.sdkKey,
});
window._optimizelyClient = optimizelyClient;

/**
 * This provider component provides the Optimizely client to all child components via React context,
 * allowing them to access and use the Optimizely client for feature flag management.
 * The {children} prop is used to render any child components passed to this component.
 */
export const FeatureFlagsProvider = ({
  children,
  optimizelyClient,
}: {
  children?: ReactNode;
  optimizelyClient: ReactSDKClient;
}) => {
  const userData = useUserDataForFeatureFlag();

  useEffect(() => {
    if (!userData) return;
    optimizelyClient.setUser(userData);
  }, [optimizelyClient, userData]);

  return <OptimizelyProvider optimizely={optimizelyClient}>{children}</OptimizelyProvider>;
};
